import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import {
  get,
  post,
  remove,
  getFileUrlByFileKey,
  fetchExternalFile,
  guessExt,
  put,
} from "../../../utils/apiUtils";
import { convertBlobToHtml } from "../../../utils/convertToHtml";

/* ---------- Types ---------- */
export type MediaRow = {
  key: string;
  document_id: string;
  file_key_id: string;
  username: string;
  document_name: string;
  document_type?: "internal" | "external";
  codes: number;
  dateCreated: string;
  _dateTs: number;
  highlightLength: number;
  modifiedBy: string;
};

type MediaState = {
  projectId: string | null;

  items: MediaRow[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;

  filterUsername: string;
  documentType?: "internal" | "external";

  viewingHtml: string | null;
  viewingDocId: string | null;
  viewingLoading: boolean;

  uploading: boolean;
  uploadProgress: number;

  deletingId: string | null;

  editingId: string | null;
  editLoading: boolean;
  updateLoading: boolean;
};

/* ---------- Initial ---------- */
const initialState: MediaState = {
  projectId: null,

  items: [],
  total: 0,
  page: 1,
  pageSize: 10,
  loading: false,

  filterUsername: "",
  documentType: undefined,

  viewingHtml: null,
  viewingDocId: null,
  viewingLoading: false,

  uploading: false,
  uploadProgress: 0,

  deletingId: null,

  editingId: null,
  editLoading: false,
  updateLoading: false,
};

/* ---------- Thunks ---------- */
export const fetchMedia = createAsyncThunk(
  "media/fetch",
  async (_: void, { getState }) => {
    const { media } = getState() as any as { media: MediaState };
    const { projectId, page, pageSize, filterUsername, documentType } = media;
    if (!projectId) throw new Error("Missing project id");

    const res = await get<any>("/documents", {
      params: {
        project_id: projectId,
        skip: (page - 1) * pageSize,
        limit: pageSize,
        username: filterUsername || undefined,
        document_type: documentType || undefined,
      },
      headers: { Accept: "application/json" },
    });
    if (res.type !== "success") throw new Error(res?.data?.message || "Failed");

    const list: any[] = res.data?.items ?? [];
    list.sort(
      (a, b) =>
        new Date(b.created_at || b.uploaded_at || 0).getTime() -
        new Date(a.created_at || a.uploaded_at || 0).getTime(),
    );

    const items: MediaRow[] = list.map((doc: any) => ({
      key: doc.document_id,
      document_id: doc.document_id,
      file_key_id: doc.file_key_id,
      username: doc.uploaded_by || "Unknown",
      document_name: doc.document_name,
      document_type: doc.document_type,
      codes: doc.num_codes || 0,
      dateCreated: dayjs(doc.uploaded_at || doc.created_at).format(
        "MM/DD/YYYY",
      ),
      _dateTs: new Date(doc.uploaded_at || doc.created_at || 0).getTime(),
      highlightLength: doc.highlight_length_count || 0,
      modifiedBy: doc.modified_by || "Unknown",
    }));

    return { items, total: res.data?.total ?? items.length };
  },
);

// --- add to thunks section ---
export const getDocumentById = createAsyncThunk(
  "media/getById",
  async (documentId: string) => {
    const res = await get<any>(`/documents/${documentId}`, {
      headers: { Accept: "application/json" },
    });
    if (res.type !== "success") throw new Error(res?.data?.message || "Failed");
    return res.data;
  },
);

export const updateDocumentName = createAsyncThunk(
  "media/updateName",
  async (payload: { documentId: string; name: string }, { dispatch }) => {
    const { documentId, name } = payload;

    const res = await put<any>(
      `/documents/${documentId}`,
      { name },
      {
        headers: { Accept: "application/json" },
      },
    );

    if (res.type !== "success")
      throw new Error(res?.data?.message || "Update failed");

    await dispatch(fetchMedia());
    return res.data;
  },
);

export const openMediaDocument = createAsyncThunk(
  "media/openDocument",
  async (payload: { fileKey: string; documentId: string }) => {
    const platformId = process.env.REACT_APP_PLATFORM_ID!;
    const { fileKey, documentId } = payload;
    if (!fileKey || !platformId)
      throw new Error("Missing file key or platform id");

    const externalUrl = await getFileUrlByFileKey(fileKey, platformId);
    if (!externalUrl) throw new Error("Could not resolve external file URL.");

    const { blob, filename, mime } = await fetchExternalFile(externalUrl);
    const ext = guessExt({ filename, mime });
    if (ext === "doc")
      throw new Error(".doc is not supported in browser. Please use .docx.");

    const html = await convertBlobToHtml(blob, ext);
    if (!html || !html.trim()) throw new Error("Failed to convert to HTML.");

    return { html, documentId };
  },
);

export const uploadInternalMedia = createAsyncThunk(
  "media/uploadInternal",
  async (
    payload: {
      projectId: string;
      userId: string;
      file: File;
      onProgress?: (p: number) => void;
    },
    { dispatch },
  ) => {
    const { projectId, userId, file, onProgress } = payload;
    const formData = new FormData();
    formData.append("file", file);

    const res = await post<any>(
      `documents/internal-upload/?project_id=${projectId}&user_id=${userId}`,
      formData,
      {
        onUploadProgress: ({ loaded, total }) => {
          const p = Math.floor((loaded / (total ?? 1)) * 100);
          dispatch(setUploadProgress(p));
          onProgress?.(p);
        },
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    if (res.type !== "success")
      throw new Error(res?.data?.message || "Upload failed");

    await dispatch(fetchMedia());
    return res.data as {
      document_id: string;
      file_key: string;
      message: string;
    };
  },
);

export const uploadExternalMedia = createAsyncThunk(
  "media/uploadExternal",
  async (
    payload: { projectId: string; userId: string; url: string },
    { dispatch },
  ) => {
    const { projectId, userId, url } = payload;
    const body = new URLSearchParams();
    body.append("url", url);

    const res = await post<any>(
      `documents/external-upload/?project_id=${projectId}&user_id=${userId}`,
      body.toString(),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
      },
    );
    if (res.type !== "success")
      throw new Error(res?.data?.message || "Upload failed");

    await dispatch(fetchMedia());
    return res.data;
  },
);

export const deleteMedia = createAsyncThunk(
  "media/delete",
  async (documentId: string, { dispatch }) => {
    const res = await remove(`/documents/${documentId}`, {
      headers: { Accept: "application/json" },
    });
    if ((res as any)?.type === "error")
      throw new Error((res as any)?.data?.message || "Delete failed");

    await dispatch(fetchMedia());
    return documentId;
  },
);

/* ---------- Slice ---------- */
const mediaSlice = createSlice({
  name: "media",
  initialState,
  reducers: {
    setMediaProjectId(state, action: PayloadAction<string | null>) {
      state.projectId = action.payload;
      state.items = [];
      state.total = 0;
      state.page = 1;
      state.viewingHtml = null;
      state.viewingDocId = null;
    },
    setMediaUsername(state, action: PayloadAction<string>) {
      state.filterUsername = action.payload;
      state.page = 1;
    },
    setMediaType(
      state,
      action: PayloadAction<"internal" | "external" | undefined>,
    ) {
      state.documentType = action.payload;
      state.page = 1;
    },
    setMediaPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setMediaPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.page = 1;
    },
    clearViewing(state) {
      state.viewingHtml = null;
      state.viewingDocId = null;
    },
    setUploadProgress(state, action: PayloadAction<number>) {
      state.uploadProgress = action.payload;
    },
    setDeleting(state, action: PayloadAction<string | null>) {
      state.deletingId = action.payload;
    },
    setEditing(state, action: PayloadAction<string | null>) {
      state.editingId = action.payload;
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchMedia.pending, (s) => {
      s.loading = true;
    });
    b.addCase(fetchMedia.fulfilled, (s, a) => {
      s.loading = false;
      s.items = a.payload.items;
      s.total = a.payload.total;
    });
    b.addCase(fetchMedia.rejected, (s) => {
      s.loading = false;
    });

    b.addCase(openMediaDocument.pending, (s) => {
      s.viewingLoading = true;
    });
    b.addCase(openMediaDocument.fulfilled, (s, a) => {
      s.viewingLoading = false;
      s.viewingHtml = a.payload.html;
      s.viewingDocId = a.payload.documentId;
    });
    b.addCase(openMediaDocument.rejected, (s) => {
      s.viewingLoading = false;
    });

    b.addCase(uploadInternalMedia.pending, (s) => {
      s.uploading = true;
      s.uploadProgress = 0;
    });
    b.addCase(uploadInternalMedia.fulfilled, (s) => {
      s.uploading = false;
      s.uploadProgress = 0;
    });
    b.addCase(uploadInternalMedia.rejected, (s) => {
      s.uploading = false;
    });

    b.addCase(uploadExternalMedia.pending, (s) => {
      s.uploading = true;
    });
    b.addCase(uploadExternalMedia.fulfilled, (s) => {
      s.uploading = false;
    });
    b.addCase(uploadExternalMedia.rejected, (s) => {
      s.uploading = false;
    });

    b.addCase(deleteMedia.fulfilled, (s, a) => {
      if (s.viewingDocId === a.payload) {
        s.viewingHtml = null;
        s.viewingDocId = null;
      }
      s.deletingId = null;
    });
    b.addCase(getDocumentById.pending, (s) => {
      s.editLoading = true;
    });
    b.addCase(getDocumentById.fulfilled, (s) => {
      s.editLoading = false;
    });
    b.addCase(getDocumentById.rejected, (s) => {
      s.editLoading = false;
    });

    b.addCase(updateDocumentName.pending, (s) => {
      s.updateLoading = true;
    });
    b.addCase(updateDocumentName.fulfilled, (s) => {
      s.updateLoading = false;
    });
    b.addCase(updateDocumentName.rejected, (s) => {
      s.updateLoading = false;
    });
  },
});

export const {
  setMediaProjectId,
  setMediaUsername,
  setMediaType,
  setMediaPage,
  setMediaPageSize,
  clearViewing,
  setUploadProgress,
  setDeleting,
  setEditing,
} = mediaSlice.actions;

export default mediaSlice.reducer;
